import type { Competitor, CompetitorDataset } from "../types";

const competitors: Competitor[] = [
  {
    id: "glossgenius",
    name: "GlossGenius",
    category: "Salon scheduling software",
    website: "https://glossgenius.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Embedded",
      breadth: 2,
      narrativeCriticality: 2,
      rationale:
        "AI helpers exist in selected workflows, but not as a core AI-native operating model",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Tiered plans",
      entryPrice: "$24/mo",
      processingFees: "2.6% flat on all transactions",
      addOnIntensity: "Low",
      marketplaceCommission: "None",
      transparency: "High",
      plans: [
        { name: "Standard", price: "$24/mo" },
        { name: "Gold", price: "$48/mo" },
      ],
    },
    differentiationNote:
      "Strong all-in-one execution; Spectra can differentiate via vertical depth and clearer ROI intelligence",
    scale: "Tens of thousands of professionals",
    countries: "US only",
    funding: "$72M+ total (Series C $28M in 2023)",
    valuation: "~$510M (2023, reported)",
  },
  {
    id: "phorest",
    name: "Phorest",
    category: "Salon, spa, and clinic management",
    website: "https://www.phorest.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Limited",
      breadth: 1,
      narrativeCriticality: 1,
      rationale:
        "Limited AI signals in retention/insights; core product remains classic workflow software",
      evidence: "Unclear",
    },
    pricing: {
      model: "Quote-based tiers",
      entryPrice: "Quote-based",
      processingFees: "Not publicly listed",
      addOnIntensity: "High",
      marketplaceCommission: "None",
      transparency: "Low",
      plans: [
        { name: "All plans", price: "Quote-based" },
      ],
    },
    differentiationNote:
      "Excellent retention and multi-location depth; Spectra can differentiate with simpler UX and stronger AI-native decision support",
    scale: "Thousands of salons",
    countries: "US, UK, Canada, Australia, Ireland, Germany, Finland, Netherlands, UAE",
    funding: "€31M+ disclosed (incl. €20M equity, €11M debt)",
    valuation: "Not publicly disclosed",
  },
  {
    id: "vagaro",
    name: "Vagaro",
    category: "Beauty, wellness, and fitness software",
    website: "https://www.vagaro.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Moderate",
      marketing: "Strong",
      ai: "Unknown",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "None",
      breadth: 0,
      narrativeCriticality: 0,
      rationale: "No public AI features; relies on traditional automation",
      evidence: "Unclear",
    },
    pricing: {
      model: "Per-location + add-ons",
      entryPrice: "$30/mo",
      processingFees: "2.2%–2.75% + $0.10–$0.19 (volume-based)",
      addOnIntensity: "High",
      marketplaceCommission: "None",
      transparency: "Medium",
      plans: [
        { name: "1 user", price: "$30/mo" },
        { name: "+1 user", price: "+$10/mo each" },
        { name: "7+ users", price: "Free after 7th" },
      ],
    },
    differentiationNote:
      "Strong breadth, hardware depth, and marketplace-led acquisition; Spectra can win with deeper salon workflow intelligence and AI-led execution",
    scale: "250K+ professionals, 162M+ appointments in 2025",
    countries: "US, Canada, Australia, UK",
    funding: "$63M+ disclosed growth equity",
    valuation: "$1B (2021, company press release)",
  },
  {
    id: "fresha",
    name: "Fresha",
    category: "Global self-care booking platform",
    website: "https://www.fresha.com",
    features: {
      booking: "Strong",
      payments: "Moderate",
      operations: "Moderate",
      clientCRM: "Moderate",
      marketing: "Strong",
      ai: "Unknown",
      analytics: "Moderate",
      teamStaff: "Moderate",
    },
    ai: {
      presence: "None",
      breadth: 0,
      narrativeCriticality: 0,
      rationale:
        "No visible AI features; marketplace discovery is the core engine",
      evidence: "Unclear",
    },
    pricing: {
      model: "Per-member + marketplace",
      entryPrice: "$29.95/mo",
      processingFees: "2.29% + $0.20 in-person, 2.79% + $0.20 online",
      addOnIntensity: "Medium",
      marketplaceCommission: "20% one-time new-client fee",
      transparency: "High",
      plans: [
        { name: "Individual", price: "$19.95/mo" },
        { name: "Team (per member)", price: "$14.95/mo each" },
        { name: "Marketplace fee", price: "20% per new client" },
      ],
    },
    differentiationNote:
      "Exceptional marketplace reach and discovery flywheel; Spectra can win with deeper salon-operator workflows and stronger decision tooling",
    scale: "450K+ professionals, 130K+ businesses, 1B+ appointments booked",
    countries: "120+ countries",
    funding: "~$184M total (incl. $31M debt in 2024)",
    valuation: "$640M+ (Dec 2021, last disclosed)",
  },
  {
    id: "boulevard",
    name: "Boulevard",
    category: "Self-care client experience platform",
    website: "https://www.joinblvd.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Embedded",
      breadth: 2,
      narrativeCriticality: 2,
      rationale:
        "Automation and optimization are strong, but in-product AI exposure appears selective vs AI-native",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Per-location tiers + add-ons",
      entryPrice: "$158/mo",
      processingFees: "2.6% + $0.10 in-person, 3.5% + $0.10 virtual",
      addOnIntensity: "Medium",
      marketplaceCommission: "None",
      transparency: "Medium",
      plans: [
        { name: "Essentials", price: "$195/mo" },
        { name: "Premier", price: "$325/mo" },
        { name: "Prestige", price: "$455/mo" },
        { name: "Enterprise", price: "Custom" },
      ],
    },
    differentiationNote:
      "Strong booking optimization and retention engine; Spectra can win via simpler setup and sharper operator intelligence",
    scale: "Thousands of salons & spas",
    countries: "US only",
    funding: "~$188M total (Series D $80M in 2025)",
    valuation: "~$800M (2025, reported)",
  },
  {
    id: "square-appointments",
    name: "Square Appointments",
    category: "Appointment scheduling and POS",
    website: "https://squareup.com/appointments",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Limited",
      breadth: 1,
      narrativeCriticality: 1,
      rationale:
        "Some AI exists in the broader ecosystem, but Appointments itself is mostly conventional automation",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Per-location tiers",
      entryPrice: "Free ($0/mo)",
      processingFees: "2.5% + $0.10 in-person (paid), 2.6% + $0.10 (free)",
      addOnIntensity: "Medium",
      marketplaceCommission: "None",
      transparency: "High",
      plans: [
        { name: "Free", price: "$0/mo" },
        { name: "Plus", price: "$29/mo per location" },
        { name: "Premium", price: "$69/mo per location" },
      ],
    },
    differentiationNote:
      "Exceptional payments and operations depth; Spectra can win with salon-specific workflows and less ecosystem complexity",
    scale: "Millions of sellers across Square ecosystem",
    countries: "US, Canada, Australia, Japan, UK",
    funding: "Square raised ~$590M pre-IPO (company-level)",
    valuation: "Block is public (market cap varies)",
  },
  {
    id: "booker",
    name: "Booker",
    category: "Spa and salon management (Mindbody)",
    website: "https://www.booker.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Embedded",
      breadth: 2,
      narrativeCriticality: 1,
      rationale:
        "AI capabilities are mostly add-on/adjacent; not clearly central in daily in-product workflows",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Tiered + sales-led",
      entryPrice: "From ~$89/mo",
      processingFees: "2.75% in-person, 3.5% + $0.15 online",
      addOnIntensity: "Medium",
      marketplaceCommission: "None",
      transparency: "Medium-Low",
      plans: [
        { name: "Starter", price: "$139/mo" },
        { name: "Accelerate", price: "$289/mo" },
        { name: "Ultimate", price: "$469/mo" },
        { name: "Ultimate Plus", price: "$599/mo" },
      ],
    },
    differentiationNote:
      "Balanced all-in-one stack with AI receptionist; Spectra can win with faster UX and more transparent automation",
    scale: "Part of Mindbody network",
    countries: "Nearly worldwide",
    funding: "~$77M raised pre-acquisition",
    valuation: "Acquired by Mindbody for ~$150M (2018)",
  },
  {
    id: "zenoti",
    name: "Zenoti",
    category: "Enterprise beauty/wellness platform",
    website: "https://www.zenoti.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Strong",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "AI-first",
      breadth: 4,
      narrativeCriticality: 4,
      rationale:
        "Most credible AI-first competitor in this set, with broader in-product AI across key workflows",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Custom pricing",
      entryPrice: "Custom quote",
      processingFees: "2.45% + $0.10 in-person, 2.95% + $0.10 online",
      addOnIntensity: "High",
      marketplaceCommission: "None",
      transparency: "Low",
      plans: [
        { name: "Salon Core", price: "~$350/mo per location" },
        { name: "Marketing Plus", price: "~$450/mo per location" },
        { name: "With Smart Bot", price: "~$500/mo per location" },
      ],
    },
    differentiationNote:
      "Very deep enterprise and AI surface area; Spectra can win on ease of use and faster time-to-value for mid-market salons",
    scale: "~30K businesses",
    countries: "~50 countries",
    funding: "$340M+ total (Series D extension $80M in 2021)",
    valuation: "~$1.5B (2021, reported)",
  },
  {
    id: "mindbody",
    name: "Mindbody",
    category: "Fitness, beauty, and wellness platform",
    website: "https://www.mindbody.com",
    features: {
      booking: "Strong",
      payments: "Strong",
      operations: "Strong",
      clientCRM: "Strong",
      marketing: "Strong",
      ai: "Moderate",
      analytics: "Strong",
      teamStaff: "Strong",
    },
    ai: {
      presence: "Limited",
      breadth: 1,
      narrativeCriticality: 1,
      rationale:
        "AI appears mainly as optional messaging add-ons, not as a native platform core",
      evidence: "Vendor-claimed",
    },
    pricing: {
      model: "Tiered + sales-led",
      entryPrice: "From ~$99/mo",
      processingFees: "2.75% in-person, 3.5% + $0.15 online",
      addOnIntensity: "High",
      marketplaceCommission: "None",
      transparency: "Medium-Low",
      plans: [
        { name: "Starter", price: "$139/mo" },
        { name: "Accelerate", price: "$289/mo" },
        { name: "Ultimate", price: "$469/mo" },
        { name: "Ultimate Plus", price: "$599/mo" },
      ],
    },
    differentiationNote:
      "Powerful marketplace and scale advantages; Spectra can win with salon-first depth and simpler daily workflows",
    scale: "Tens of thousands of businesses",
    countries: "130+ countries",
    funding: "Public company historically; later acquired private",
    valuation: "Acquired by Vista for ~$1.9B (2019)",
  },
];

const dataset: CompetitorDataset = {
  competitors,
  lastUpdated: "2026-02-22",
};

export function getCompetitorData(): CompetitorDataset {
  return dataset;
}
